import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  MuiThemeProvider,
  createMuiTheme,
} from '@material-ui/core'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { deleteTicket, getTicketById, getTicketAvailability, updateTicket } from '../../../../api/ticket'
import { getDepartmentsAdmin } from '../../../../api/department'
import { SERVER_URL } from '../../../../api'
import { useSkeleton } from '../../../hooks/useSkeleton'
import { alertError, alertSuccess } from '../../../../utils/logger'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import {
  editPathForKind,
  listPathForKind,
  MUNICIPALITY_OPTIONS,
  INCIDENT_CATEGORIES,
  CITA_STATUS_LABEL,
  siteNamesFromSites,
} from './ticketTableUtils'
import IncidentLocationMap, {
  parseLegacyCoords,
  stripLegacyCoords,
} from './IncidentLocationMap'

const theme = createMuiTheme({
  palette: { secondary: { main: '#F64E60' } },
})

function resolveTicketImageUrl(image) {
  if (!image) return ''
  if (
    image.startsWith('data:') ||
    image.startsWith('http://') ||
    image.startsWith('https://') ||
    image.startsWith('blob:')
  ) {
    return image
  }
  const path = image.startsWith('/') ? image : `/${image}`
  return `${SERVER_URL}${path}`
}

const selectMenuProps = {
  style: { zIndex: 2000 },
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
}

const summaryCardStyle = {
  background: 'rgba(90, 90, 64, 0.05)',
  border: '1px solid rgba(90, 90, 64, 0.15)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 20,
}

const statusPillStyle = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: '#5A5A40',
  background: 'rgba(90, 90, 64, 0.1)',
  borderRadius: 999,
  padding: '4px 10px',
  marginBottom: 8,
}

const slotButtonStyle = (selected) => ({
  minWidth: 88,
  height: 44,
  borderRadius: 10,
  border: selected ? '1px solid #5A5A40' : '1px solid #EAE6DF',
  background: selected ? '#5A5A40' : '#FDFCF9',
  color: selected ? '#fff' : '#181C32',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
})

function expectedKindFromPath(pathname) {
  if (pathname.startsWith('/edit-cita')) return 'appointment'
  if (pathname.startsWith('/edit-sugerencia')) return 'suggestion'
  if (pathname.startsWith('/edit-incidencia')) return 'incident'
  return null
}

function listPathForExpectedKind(expected) {
  if (expected === 'appointment') return '/citas'
  if (expected === 'suggestion') return '/sugerencias'
  return '/incidencias'
}

function resolveTicketGeo(data) {
  if (data.lat != null && data.lng != null) {
    return {
      lat: Number(data.lat),
      lng: Number(data.lng),
      notes: stripLegacyCoords(data.notes || ''),
    }
  }
  const legacy = parseLegacyCoords(data.notes)
  if (legacy) {
    return {
      lat: legacy.lat,
      lng: legacy.lng,
      notes: stripLegacyCoords(data.notes || ''),
    }
  }
  return { lat: null, lng: null, notes: data.notes || '' }
}

function normalizeTimeValue(time) {
  if (!time) return ''
  const m = String(time).match(/^(\d{1,2}):(\d{2})/)
  if (!m) return String(time)
  return `${m[1].padStart(2, '0')}:${m[2]}`
}

export default function EditTicketPage() {
  const [ticket, setTicket] = useState(null)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [departments, setDepartments] = useState([])
  const [availableSlots, setAvailableSlots] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const ticketId = useParams().id
  const history = useHistory()
  const location = useLocation()
  const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } = useSkeleton()
  const expectedKind = expectedKindFromPath(location.pathname)

  const isAppointment = ticket?.kind === 'appointment'
  const isSuggestion = ticket?.kind === 'suggestion'
  const isIncident = ticket?.kind === 'incident'
  const listPath = ticket ? listPathForKind(ticket.kind) : listPathForExpectedKind(expectedKind)

  useEffect(() => {
    if (!ticketId) {
      history.push(listPathForExpectedKind(expectedKind))
      return
    }
    getTicketById(ticketId)
      .then((res) => {
        if (res.status === 200) {
          const data = res.data
          if (expectedKind && data.kind !== expectedKind) {
            history.replace(editPathForKind(data.kind, data._id))
            return
          }
          const geo = resolveTicketGeo(data)
          setTicket({ ...data, lat: geo.lat, lng: geo.lng, notes: geo.notes })
          disableLoadingData()
        }
      })
      .catch((error) => {
        alertError({ error, customMessage: 'No se pudo cargar el ticket.' })
        history.push(listPathForExpectedKind(expectedKind))
      })
  }, [ticketId, disableLoadingData, history, expectedKind])

  useEffect(() => {
    if (!isAppointment) return
    getDepartmentsAdmin()
      .then((res) => {
        if (res.status === 200) {
          setDepartments(Array.isArray(res.data) ? res.data : [])
        }
      })
      .catch(() => {
        setDepartments([])
      })
  }, [isAppointment])

  useEffect(() => {
    if (!isAppointment || !ticket?.serviceName || !ticket?.date) {
      setAvailableSlots([])
      return
    }

    const dept = departments.find((d) => d.name === ticket.serviceName)
    const requiredSites = siteNamesFromSites(dept?.sites)
    if (requiredSites.length > 0 && !ticket.site) {
      setAvailableSlots([])
      return
    }

    let cancelled = false
    setLoadingSlots(true)
    getTicketAvailability(ticket.serviceName, ticket.date, ticket.site || '')
      .then((res) => {
        if (cancelled) return
        const slots = res.status === 200 && Array.isArray(res.data?.slots) ? res.data.slots : []
        setAvailableSlots(slots)
      })
      .catch(() => {
        if (!cancelled) setAvailableSlots([])
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false)
      })

    return () => {
      cancelled = true
    }
  }, [isAppointment, ticket?.serviceName, ticket?.date, ticket?.site, departments])

  function patch(field, value) {
    setTicket((prev) => ({ ...prev, [field]: value }))
  }

  function setLocation(la, ln) {
    setTicket((prev) => ({ ...prev, lat: la, lng: ln }))
  }

  function clearLocation() {
    setTicket((prev) => ({ ...prev, lat: null, lng: null }))
  }

  function save() {
    const body = isAppointment
      ? {
          status: ticket.status,
          notes: ticket.notes,
          serviceName: ticket.serviceName,
          site: ticket.site || '',
          date: ticket.date,
          time: normalizeTimeValue(ticket.time),
        }
      : {
          status: ticket.status,
          notes: stripLegacyCoords(ticket.notes || ''),
          publicFeedback: ticket.publicFeedback,
          title: ticket.title,
          municipality: ticket.municipality,
          ...(isIncident
            ? {
                category: ticket.category,
                lat: ticket.lat,
                lng: ticket.lng,
              }
            : {}),
        }

    updateTicket(ticketId, body)
      .then((res) => {
        if (res.status === 200) {
          alertSuccess({ title: 'Guardado', customMessage: 'Ticket actualizado.' })
          history.push(listPathForKind(ticket.kind))
        }
      })
      .catch((error) => {
        const status = error?.response?.status
        const message = error?.response?.data?.message
        if (status === 409) {
          alertError({ error, customMessage: message || 'Ese horario ya está reservado.' })
          return
        }
        alertError({ error, customMessage: 'No se pudieron guardar los cambios.' })
      })
  }

  const departmentOptions = useMemo(() => {
    const names = departments.map((d) => d.name).filter(Boolean)
    if (ticket?.serviceName && !names.includes(ticket.serviceName)) {
      return [ticket.serviceName, ...names]
    }
    return names
  }, [departments, ticket?.serviceName])

  const siteOptions = useMemo(() => {
    const dept = departments.find((d) => d.name === ticket?.serviceName)
    const sites = siteNamesFromSites(dept?.sites)
    if (ticket?.site && !sites.includes(ticket.site)) {
      return [ticket.site, ...sites]
    }
    return sites
  }, [departments, ticket?.serviceName, ticket?.site])

  const slotsToShow = useMemo(() => {
    const current = normalizeTimeValue(ticket?.time)
    if (current && !availableSlots.includes(current)) {
      return [current, ...availableSlots]
    }
    return availableSlots
  }, [availableSlots, ticket?.time])

  if (isLoadingData || !ticket) return <ContentSkeleton />

  const municipalityOptions = MUNICIPALITY_OPTIONS.includes(ticket.municipality)
    ? MUNICIPALITY_OPTIONS
    : ticket.municipality
      ? [ticket.municipality, ...MUNICIPALITY_OPTIONS]
      : MUNICIPALITY_OPTIONS

  const categoryOptions = INCIDENT_CATEGORIES.includes(ticket.category)
    ? INCIDENT_CATEGORIES
    : ticket.category
      ? [ticket.category, ...INCIDENT_CATEGORIES]
      : INCIDENT_CATEGORIES

  const hasLocation =
    ticket.lat != null &&
    ticket.lng != null &&
    Number.isFinite(Number(ticket.lat)) &&
    Number.isFinite(Number(ticket.lng))

  const citaStatusLabel = CITA_STATUS_LABEL[ticket.status] || ticket.status || '—'
  const selectedTime = normalizeTimeValue(ticket.time)

  return (
    <>
      <Card>
        <CardHeader
          title={
            isAppointment
              ? `Cita previa ${ticket.ref || ticket._id}`
              : `${isSuggestion ? 'Sugerencia' : 'Incidencia'} ${ticket.ref || ticket._id}`
          }
        />
        <CardBody>
          {isAppointment ? (
            <>
              <div style={summaryCardStyle}>
                <span style={statusPillStyle}>{citaStatusLabel}</span>
                <h4 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#181C32' }}>
                  {ticket.serviceName || 'Sin servicio'}
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: 13, color: '#7E8299', fontWeight: 500 }}>
                  <span>{ticket.site || 'Sin centro'}</span>
                  <span>{ticket.date || 'Sin fecha'}</span>
                  <span>{selectedTime ? `${selectedTime}h` : 'Sin hora'}</span>
                </div>
                <div style={{ marginTop: 12, fontSize: 13, color: '#3F4254' }}>
                  <div><strong>Ciudadano:</strong> {ticket.fullName || '—'}</div>
                  <div><strong>Email:</strong> {ticket.email || '—'}</div>
                  {ticket.ref ? <div><strong>Ref:</strong> {ticket.ref}</div> : null}
                </div>
              </div>

              <p style={{ fontSize: 13, color: '#7E8299', marginBottom: 8 }}>
                Datos de la reserva (igual que ve el ciudadano al agendar).
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <FormControl variant="outlined" margin="normal" fullWidth>
                  <InputLabel id="ticket-service-label">Servicio Administrativo</InputLabel>
                  <Select
                    labelId="ticket-service-label"
                    value={ticket.serviceName || ''}
                    onChange={(e) => {
                      patch('serviceName', e.target.value)
                      patch('site', '')
                      patch('municipality', '')
                      patch('time', '')
                    }}
                    label="Servicio Administrativo"
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">—</MenuItem>
                    {departmentOptions.map((name) => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {siteOptions.length > 0 ? (
                  <FormControl variant="outlined" margin="normal" fullWidth>
                    <InputLabel id="ticket-site-label">Sitio / centro</InputLabel>
                    <Select
                      labelId="ticket-site-label"
                      value={ticket.site || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        patch('site', value)
                        patch('time', '')
                      }}
                      label="Sitio / centro"
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="">—</MenuItem>
                      {siteOptions.map((name) => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
              </div>

              <TextField
                label="Fecha deseada"
                type="date"
                value={ticket.date || ''}
                onChange={(e) => {
                  patch('date', e.target.value)
                  patch('time', '')
                }}
                margin="normal"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Al cambiar la fecha o el sitio se muestran las horas libres."
              />

              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#7E8299', textTransform: 'uppercase' }}>
                    Hora disponible
                  </label>
                  {selectedTime ? (
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#5A5A40', background: 'rgba(90,90,64,0.1)', borderRadius: 999, padding: '2px 8px' }}>
                      Seleccionada: {selectedTime}
                    </span>
                  ) : null}
                </div>

                {!ticket.serviceName || !ticket.date || (siteOptions.length > 0 && !ticket.site) ? (
                  <div style={{ border: '1px dashed #EAE6DF', borderRadius: 12, padding: 20, textAlign: 'center', background: '#FDFCF9', color: '#7E8299' }}>
                    {siteOptions.length > 0 && !ticket.site
                      ? 'Elige servicio, centro y fecha para ver los huecos libres'
                      : 'Elige servicio y fecha para ver los huecos libres'}
                  </div>
                ) : loadingSlots ? (
                  <p style={{ color: '#7E8299', fontSize: 13 }}>Cargando horas disponibles…</p>
                ) : slotsToShow.length === 0 ? (
                  <div style={{ border: '1px solid #FFA800', borderRadius: 12, padding: 16, background: '#FFF4DE', color: '#B78103' }}>
                    <strong>Sin huecos libres este día.</strong>
                    <div style={{ fontSize: 13, marginTop: 4 }}>Prueba con otra fecha o servicio.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="listbox" aria-label="Horas disponibles">
                    {slotsToShow.map((slot) => {
                      const selected = selectedTime === slot
                      return (
                        <button
                          key={slot}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => patch('time', slot)}
                          style={slotButtonStyle(selected)}
                        >
                          {slot}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              <TextField
                label="Notas o motivos del trámite (opcional)"
                value={ticket.notes || ''}
                onChange={(e) => patch('notes', e.target.value)}
                margin="normal"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                placeholder="Documentación especial, intérprete, motivo del trámite…"
                helperText="Es el mismo campo que rellena el ciudadano en la web al agendar."
              />

              <FormControl variant="outlined" margin="normal" fullWidth>
                <InputLabel id="ticket-status-cita-label">Estado</InputLabel>
                <Select
                  labelId="ticket-status-cita-label"
                  value={ticket.status || 'pending'}
                  onChange={(e) => patch('status', e.target.value)}
                  label="Estado"
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="in_progress">Confirmada</MenuItem>
                  <MenuItem value="resolved">Completada</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </>
          ) : (
            <>
              <p>
                <strong>Tipo:</strong>{' '}
                {ticket.kind === 'suggestion' ? 'Sugerencia' : 'Incidencia'}
              </p>
              <p><strong>Nombre:</strong> {ticket.fullName || '—'}</p>
              <p><strong>Email:</strong> {ticket.email || '—'}</p>

              <FormControl variant="outlined" margin="normal" fullWidth>
                <InputLabel id="ticket-municipality-label">Municipio</InputLabel>
                <Select
                  labelId="ticket-municipality-label"
                  value={ticket.municipality || ''}
                  onChange={(e) => patch('municipality', e.target.value)}
                  label="Municipio"
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="">—</MenuItem>
                  {municipalityOptions.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Título"
                value={ticket.title || ''}
                onChange={(e) => patch('title', e.target.value)}
                margin="normal"
                variant="outlined"
                fullWidth
              />
              {isIncident ? (
                <FormControl variant="outlined" margin="normal" fullWidth>
                  <InputLabel id="ticket-category-label">Categoría</InputLabel>
                  <Select
                    labelId="ticket-category-label"
                    value={ticket.category || ''}
                    onChange={(e) => patch('category', e.target.value)}
                    label="Categoría"
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">—</MenuItem>
                    {categoryOptions.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <p><strong>Descripción:</strong></p>
              <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.description || '—'}</p>
              {isIncident && ticket.image ? (
                <p>
                  <img
                    src={resolveTicketImageUrl(ticket.image)}
                    alt=""
                    style={{ maxWidth: '100%', maxHeight: 480, objectFit: 'contain' }}
                  />
                </p>
              ) : null}

              <FormControl variant="outlined" margin="normal" fullWidth>
                <InputLabel id="ticket-status-label">Estado</InputLabel>
                <Select
                  labelId="ticket-status-label"
                  value={ticket.status || 'pending'}
                  onChange={(e) => patch('status', e.target.value)}
                  label="Estado"
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="in_progress">En trámite</MenuItem>
                  <MenuItem value="resolved">Resuelta</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Respuesta al ciudadano"
                value={ticket.publicFeedback || ''}
                onChange={(e) => patch('publicFeedback', e.target.value)}
                margin="normal"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                helperText="Visible para el ciudadano en su perfil."
              />

              {isIncident ? (
                <>
                  <p style={{ marginTop: 16 }}><strong>Ubicación</strong></p>
                  {hasLocation ? (
                    <p style={{ fontSize: 13, color: '#7E8299' }}>
                      {Number(ticket.lat).toFixed(6)}, {Number(ticket.lng).toFixed(6)}
                      {' · '}
                      <a
                        href={`https://www.openstreetmap.org/?mlat=${ticket.lat}&mlon=${ticket.lng}#map=16/${ticket.lat}/${ticket.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir en OpenStreetMap
                      </a>
                      {' · '}
                      <a
                        href={`https://www.google.com/maps?q=${ticket.lat},${ticket.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Google Maps
                      </a>
                    </p>
                  ) : (
                    <p style={{ fontSize: 13, color: '#7E8299' }}>
                      Sin coordenadas. Haz clic en el mapa para marcar el punto.
                    </p>
                  )}
                  <IncidentLocationMap
                    lat={ticket.lat}
                    lng={ticket.lng}
                    editable
                    onChange={setLocation}
                    height={300}
                  />
                  {hasLocation ? (
                    <Button
                      size="small"
                      onClick={clearLocation}
                      style={{ marginTop: 8 }}
                    >
                      Quitar ubicación
                    </Button>
                  ) : null}
                </>
              ) : null}

              <TextField
                label="Notas internas"
                value={ticket.notes || ''}
                onChange={(e) => patch('notes', e.target.value)}
                margin="normal"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                helperText={
                  isIncident
                    ? 'Solo uso interno. La ubicación se gestiona en el mapa, no aquí.'
                    : undefined
                }
              />
            </>
          )}
        </CardBody>
      </Card>
      <Button onClick={() => history.push(listPath)} variant="outlined" style={{ marginRight: 20 }}>
        Volver
      </Button>
      <Button onClick={save} variant="outlined" color="primary" style={{ marginRight: 20 }}>
        Guardar
      </Button>
      <MuiThemeProvider theme={theme}>
        <Button onClick={() => setOpenConfirmDialog(true)} variant="outlined" color="secondary">
          Eliminar
        </Button>
      </MuiThemeProvider>
      <ConfirmDialog
        title="¿Eliminar este ticket?"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          const backPath = listPathForKind(ticket.kind)
          deleteTicket(ticketId)
            .then((res) => {
              if (res.status === 200 || res.status === 204) {
                alertSuccess({ title: 'Eliminado', customMessage: 'Ticket eliminado.' })
                history.push(backPath)
              }
            })
            .catch((error) => {
              alertError({ error, customMessage: 'No se pudo eliminar.' })
            })
        }}
      />
    </>
  )
}
